/* eslint-disable camelcase */
/* eslint-disable import/prefer-default-export */
import { parse, toSeconds } from 'iso8601-duration';

// actions
import changeSettings from 'actions/user/changeSettings';
import claimHomeConnectPremiumAccessPromo from 'actions/user/claimHomeConnectPremiumAccessPromo';

// services
import api from 'services/homeconnect';
import { convertTemperature } from 'services/units';
import { getAuthToken } from 'services/authTokenForApp';

// constants
import {
  APPLIANCE_ADJUSTMENT_START,
  APPLIANCE_ADJUSTMENT_SUCCESS,
  APPLIANCE_ADJUSTMENT_ERROR,
  GET_APPLIANCES_START,
  GET_APPLIANCES_SUCCESS,
  GET_APPLIANCES_ERROR,
  GET_APPLIANCE_STATUS_START,
  GET_APPLIANCE_STATUS_ERROR,
  GET_APPLIANCE_STATUS_SUCCESS,
  GET_APPLIANCE_PROGRAMS_START,
  GET_APPLIANCE_PROGRAMS_ERROR,
  GET_APPLIANCE_PROGRAMS_SUCCESS,
  STOP_APPLIANCE_START,
  STOP_APPLIANCE_SUCCESS,
  STOP_APPLIANCE_ERROR,
  HOME_CONNECT_DISCONNECT,
  HOME_CONNECT_EVENT_CHANNEL_OPEN,
  HOME_CONNECT_EVENT_CHANNEL_CLOSE,
  HOME_CONNECT_NOTIFICATION,
  HOME_CONNECT_EVENT_CHANNEL_CONNECTING,
  HOME_CONNECT_EVENT_CHANNEL_ERROR,
  HOME_CONNECT_SET_RECIPE,
  HOME_CONNECT_CLEAR_RECIPE,
  HOME_CONNECT_NEXT_STEP,
  HOME_CONNECT_SET_CURRENT_STEP,
  HOME_CONNECT_RESET,
  SET_PRIMARY_OVEN,
} from '../constants';
import { VALID_APPLIANCE_TYPES } from '../constants/content';
import { refreshUser } from './user/refreshUser';

const pollTimers = {};
const eventSources = {};

function getConfig(getState) {
  const token = getState().user.home_connect_token;
  const { hostname, appliances, currentStep } = getState().homeConnect;

  return { token, hostname, appliances, currentStep };
}

export function setRecipe({
  id,
  title,
  appliance_adjustments,
  appliance_adjustments_ok,
  cooking_tips,
}) {
  return async (dispatch, getState) => {
    if (!appliance_adjustments || !appliance_adjustments.length) {
      return;
    }
    const { recipeInProgress } = getState().homeConnect;
    await dispatch({
      type: HOME_CONNECT_SET_RECIPE,
      payload: { id, title, appliance_adjustments, appliance_adjustments_ok, cooking_tips },
    });
    if (!recipeInProgress) {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(
          'homeConnect.recipe',
          JSON.stringify(getState().homeConnect.recipe),
        );
      }
    }
  };
}

function checkTemperatureInSupportedRange(params, programInfo) {
  const warnings = [];
  const temperatureOption = programInfo.options.find(
    option => option.key === 'Cooking.Oven.Option.SetpointTemperature',
  );
  let setpointUnit = params.unit || '°C';
  let setpointTemp = params.value || 0;
  if (temperatureOption) {
    setpointUnit = temperatureOption.unit;
    if (params.temperature_c && params.temperature_f) {
      // New way
      setpointTemp = setpointUnit === '°C' ? params.temperature_c : params.temperature_f;
    } else {
      // Old way
      setpointTemp = convertTemperature(params.unit, setpointUnit, params.value);
    }
    if (setpointTemp > temperatureOption.constraints.max) {
      setpointTemp = temperatureOption.constraints.max;
      warnings.push(`Using oven max. temp. of ${temperatureOption.constraints.max}${setpointUnit}`);
    } else if (setpointTemp < temperatureOption.constraints.min) {
      // FIXME we should give an error here to stop things burning.
      setpointTemp = temperatureOption.constraints.min;
      warnings.push(`Using oven min. temp. of ${temperatureOption.constraints.min}${setpointUnit}`);
    }
    const stepSize = temperatureOption.constraints.stepsize;
    if (stepSize > 1 && setpointTemp % stepSize !== 0) {
      // We need to round the temperature to the nearest stepsize
      setpointTemp = Math.round(setpointTemp / stepSize) * stepSize;
      warnings.push(`HomeConnect rounded temp. to ${setpointTemp}${setpointUnit}`);
    }
  }
  return { setpointUnit, setpointTemp, warnings };
}

export function updateStatusItem({ haId, key }) {
  return async (dispatch, getState) => {
    const { token, hostname } = getConfig(getState);
    try {
      const response = await api.getApplianceStatus({
        token,
        hostname,
        haId,
        key,
      });
      dispatch({
        type: GET_APPLIANCE_STATUS_SUCCESS,
        payload: { haId, status: response },
      });
    } catch (error) {
      dispatch({
        type: GET_APPLIANCE_STATUS_ERROR,
        payload: { haId, error: error.cause || error },
      });
      console.error('HomeConnect: Failed to update status of', key, error);
    }
  };
}

function startPolling(dispatch, getState, { haId, key, interval = 60000 }) {
  pollTimers[`${haId}-${key}`] = setTimeout(() => {
    const { operationState } = getState().homeConnect.appliances[haId];
    if (operationState === 'Run' || operationState === 'Pause') {
      dispatch(updateStatusItem({ haId, key }));
    }
    startPolling(dispatch, getState, { haId, key, interval });
  }, interval);
}

export function getApplianceStatus(params = {}) {
  return async (dispatch, getState) => {
    const { token, hostname, currentStep } = getConfig(getState);
    const { haId } = params;
    if (!token) {
      return;
    }
    dispatch({
      type: GET_APPLIANCE_STATUS_START,
      payload: { haId },
    });

    try {
      const [status, activeProgram, selectedProgram, settings] = await Promise.all([
        api.getApplianceStatus({ token, hostname, haId }),
        api.getActiveProgram({ token, hostname, haId }),
        api.getSelectedProgram({ token, hostname, haId }),
        api.getApplianceSettings({ token, hostname, haId }),
      ]);

      // Get the current program constraints
      let warnings = [];
      let programInfo;
      if (activeProgram) {
        programInfo = await api.getApplianceProgram({
          hostname,
          haId,
          token,
          program: activeProgram.key,
        });

        if (currentStep && currentStep.setting) {
          warnings = checkTemperatureInSupportedRange(currentStep.setting, programInfo).warnings;
          if (currentStep.setting.mode === 'preheat' || currentStep.setting.rapidHeat) {
            // eslint-disable-next-line no-use-before-define
            stopPolling();
            startPolling(dispatch, getState, {
              haId,
              key: 'Cooking.Oven.Status.CurrentCavityTemperature',
              interval: 60000,
            });
          }
        }
      }

      dispatch({
        type: GET_APPLIANCE_STATUS_SUCCESS,
        payload: { haId, status, activeProgram, selectedProgram, settings, programInfo, warnings },
      });
    } catch (error) {
      dispatch({
        type: GET_APPLIANCE_STATUS_ERROR,
        payload: { haId, error: error.cause || error },
      });
      console.error('HomeConnect: Failed to get appliance status', error);
    }
  };
}

export function getAppliancePrograms(params = {}) {
  return async (dispatch, getState) => {
    const { token, hostname } = getConfig(getState);
    const { haId } = params;
    dispatch({
      type: GET_APPLIANCE_PROGRAMS_START,
      payload: { haId },
    });

    try {
      const programs = await api.getAppliancePrograms({ token, hostname, haId });

      dispatch({
        type: GET_APPLIANCE_PROGRAMS_SUCCESS,
        payload: { haId, programs },
      });
    } catch (error) {
      dispatch({
        type: GET_APPLIANCE_PROGRAMS_ERROR,
        payload: { haId, error: error.cause || error },
      });
      console.error('HomeConnect: Failed to get appliance programs', error);
    }
  };
}

export function stopAppliance(params = {}) {
  return async (dispatch, getState) => {
    const { token, hostname } = getConfig(getState);
    const { haId } = params;
    dispatch({
      type: STOP_APPLIANCE_START,
      payload: { haId },
    });

    try {
      await api.stopActiveProgram({ hostname, haId, token });
      dispatch({
        type: STOP_APPLIANCE_SUCCESS,
        payload: { haId },
      });
    } catch (error) {
      dispatch({
        type: STOP_APPLIANCE_ERROR,
        payload: { haId, error: error.cause || error },
      });
      console.error('HomeConnect: Failed to stop program', error);
    }
  };
}

function stopPolling() {
  Object.values(pollTimers).forEach(timer => {
    clearTimeout(timer);
  });
}

export function setPrimaryOven({ haId }) {
  return async (dispatch, getState) => {
    await dispatch({
      type: SET_PRIMARY_OVEN,
      payload: { haId },
    });
    const { primaryOven } = getState().homeConnect;
    dispatch(changeSettings({ home_connect_preferred_oven: haId }));
    if (primaryOven && primaryOven.connected) {
      dispatch(getApplianceStatus(primaryOven));
      dispatch(getAppliancePrograms(primaryOven));
      // Stop monitoring the other oven(s).
      // In future we might want to monitor multiple ovens, but for now lets keep it simple,
      Object.keys(eventSources).forEach(id => {
        eventSources[id].close();
        delete eventSources[id];
      });
      // eslint-disable-next-line no-use-before-define
      dispatch(monitorAppliance(primaryOven));
    }
  };
}

export function getAppliances() {
  return async (dispatch, getState) => {
    const { token, hostname } = getConfig(getState);
    if (!token) {
      return;
    }
    dispatch({
      type: GET_APPLIANCES_START,
    });

    try {
      const appliances = await api.getAppliances({ hostname, token });

      const { home_connect_preferred_oven } = getState().user;
      const preferredOven = appliances.find(
        appliance => appliance.haId === home_connect_preferred_oven && appliance.type === 'Oven',
      );

      await dispatch({
        type: GET_APPLIANCES_SUCCESS,
        payload: { appliances },
      });
      const firstConnectedOven = appliances.find(appliance => appliance.type === 'Oven');
      if (preferredOven || firstConnectedOven) {
        dispatch(setPrimaryOven(preferredOven || firstConnectedOven));
      }

      // Check for promotions against each oven brand
      const brands = Array.from(new Set(appliances.map(appliance => appliance.brand)));
      brands.forEach(brand => {
        dispatch(
          claimHomeConnectPremiumAccessPromo(brand, {
            silent: true,
            position: getState().dimensions.displayType === 'mobile' ? 'bottom-right' : 'top-left',
            message: `Claiming ${brand} promotion...`,
          }),
        );
      });
    } catch (error) {
      dispatch({
        type: GET_APPLIANCES_ERROR,
        payload: { error: error.cause || error },
      });
      if (error.cause && error.cause.key === 'invalid_token') {
        dispatch(changeSettings({ home_connect_token: null }));
      }
      console.error('HomeConnect: Failed to get appliances', error);
    }
  };
}

function getProgramIfAvailable(appliance, ...programKeys) {
  if (appliance && appliance.programs) {
    const found = programKeys.find(programKey =>
      appliance.programs.find(
        program => program.key === programKey && program.constraints.available,
      ),
    );
    if (found) {
      return found;
    }
    // Some Gaggenau ovens don't support TopBottomHeating
    // https://trello.com/c/fMSwrKUp
    return [
      'Cooking.Oven.Program.HeatingMode.TopBottomHeating',
      'Cooking.Oven.Program.HeatingMode.HotAir',
    ].find(programKey =>
      appliance.programs.find(
        program => program.key === programKey && program.constraints.available,
      ),
    );
  }
  // Last resort - just heat it up
  return 'Cooking.Oven.Program.HeatingMode.TopBottomHeating';
}

function selectProgramForAdjustment({ type, mode }, appliance) {
  if (VALID_APPLIANCE_TYPES.includes(type) && mode) {
    if (type === 'sousvide') {
      return getProgramIfAvailable(
        appliance,
        'Cooking.Oven.Program.SteamModes.SousVide',
        'Cooking.Oven.Program.HeatingMode.VaccumCooking',
      );
    }
    if (type === 'steamer') {
      return getProgramIfAvailable(
        appliance,
        'Cooking.Oven.Program.SteamModes.Steam',
        'Cooking.Oven.Program.HeatingMode.SteamWithSteamSet',
      );
    }
    switch (mode.toLowerCase().replace(/[^a-z0-9]/, '')) {
      case 'preheat':
        return 'Cooking.Oven.Program.HeatingMode.PreHeating';

      case 'pizza':
      case 'bakingstone':
        return getProgramIfAvailable(appliance, 'Cooking.Oven.Program.HeatingMode.PizzaSetting');

      case 'circothermintensive':
        return getProgramIfAvailable(
          appliance,
          'Cooking.Oven.Program.HeatingMode.IntensiveHeat',
          'Cooking.Oven.Program.HeatingMode.PizzaSetting',
        );

      case 'roast':
      case 'circothermroast': // NEFF
      case 'circoroast': // NEFF
      case 'fullsurfacegrillair': // Gaggenau
        return getProgramIfAvailable(appliance, 'Cooking.Oven.Program.HeatingMode.HotAirGrilling');

      case 'grill': // NEFF
      case 'grilllargearea': // NEFF
      case 'fullsurfacegrill': // Gaggenau
        return 'Cooking.Oven.Program.HeatingMode.GrillLargeArea';

      case 'grillsmall': // NEFF
      case 'compactgrill': // Gaggenau
        return 'Cooking.Oven.Program.HeatingMode.GrillSmallArea';

      case 'doughproving':
      case 'prove':
      case 'proof':
        return 'Cooking.Oven.Program.SteamModes.DoughProving';

      case 'topbottom':
      case 'topbottomheating':
      case 'topbottomheat':
      default:
        return 'Cooking.Oven.Program.HeatingMode.TopBottomHeating';

      case 'bottom':
      case 'bottomheat':
      case 'bottomheating':
        return getProgramIfAvailable(appliance, 'Cooking.Oven.Program.HeatingMode.BottomHeating');

      case 'lowtemperature':
      case 'slowcook':
        return getProgramIfAvailable(
          appliance,
          'Cooking.Oven.Program.HeatingMode.SlowCook',
          'Cooking.Oven.Program.HeatingMode.SlowCookSteam',
        );

      case 'bake':
      case 'circotherm': // NEFF
      case 'hotair': // Gaggenau
      case 'fourdhotair': // Siemens
        return 'Cooking.Oven.Program.HeatingMode.HotAir';

      case 'hotaireco': // Gaggenau
        return 'Cooking.Oven.Program.HeatingMode.HotAirEco';

      case 'defrosting':
      case 'defrost':
        return 'Cooking.Oven.Program.HeatingMode.Defrost';

      case 'keepwarm':
        return 'Cooking.Oven.Program.HeatingMode.KeepWarm';

      case 'bread':
      case 'breadbaking':
        return 'Cooking.Oven.Program.HeatingMode.BreadBaking';

      case 'sousvide':
        return getProgramIfAvailable(
          appliance,
          'Cooking.Oven.Program.SteamModes.SousVide',
          'Cooking.Oven.Program.HeatingMode.VaccumCooking',
        );

      case 'steam':
      case 'fullsteam':
        return getProgramIfAvailable(
          appliance,
          'Cooking.Oven.Program.SteamModes.Steam',
          'Cooking.Oven.Program.HeatingMode.SteamWithSteamSet',
          'Cooking.Oven.Program.HeatingMode.HotAir100Steam',
        );

      case 'topthirdbottomheat':
        return getProgramIfAvailable(
          appliance,
          'Cooking.Oven.Program.HeatingMode.TopThirdBottomHeat',
        );

      case 'topheat':
      case 'topheating':
        return getProgramIfAvailable(appliance, 'Cooking.Oven.Program.HeatingMode.TopHeat');

      case 'thirdtopbottomheat':
      case 'thirdtopbottomheating':
        return getProgramIfAvailable(
          appliance,
          'Cooking.Oven.Program.HeatingMode.ThirdTopBottomHeat',
        );

      case 'hotairbottomheat':
      case 'hotairbottomheating':
        return getProgramIfAvailable(
          appliance,
          'Cooking.Oven.Program.HeatingMode.HotAirBottomHeat',
        );

      case 'hotairthirdbottomheat':
      case 'hotairthirdbottomheating':
        return getProgramIfAvailable(
          appliance,
          'Cooking.Oven.Program.HeatingMode.HotAirThirdBottomHeat',
        );

      case 'roaster':
        return getProgramIfAvailable(
          appliance,
          'Cooking.Oven.Program.HeatingMode.Roaster',
          'Cooking.Oven.Program.HeatingMode.HotAirGrilling',
        );
      case 'hotair100steam':
        return getProgramIfAvailable(appliance, 'Cooking.Oven.Program.HeatingMode.HotAir100Steam');
      case 'hotair80steam':
        return getProgramIfAvailable(appliance, 'Cooking.Oven.Program.HeatingMode.HotAir80Steam');
      case 'hotair60steam':
        return getProgramIfAvailable(appliance, 'Cooking.Oven.Program.HeatingMode.HotAir60Steam');
      case 'hotair30steam':
        return getProgramIfAvailable(appliance, 'Cooking.Oven.Program.HeatingMode.HotAir30Steam');
      case 'fullgrill01steam':
        return getProgramIfAvailable(
          appliance,
          'Cooking.Oven.Program.HeatingMode.FullGrill01Steam',
        );
      case 'fullgrill02steam':
        return getProgramIfAvailable(
          appliance,
          'Cooking.Oven.Program.HeatingMode.FullGrill02Steam',
        );
      case 'regenerating':
        return getProgramIfAvailable(appliance, 'Cooking.Oven.Program.HeatingMode.Regenerating');
    }
  }
  return 'Cooking.Oven.Program.HeatingMode.TopBottomHeating';
}

export function adjustAppliance(params) {
  return async (dispatch, getState) => {
    const { token, hostname, appliances } = getConfig(getState);
    const { haId } = params;
    const appliance = appliances[haId];
    if (!appliance) {
      dispatch({
        type: APPLIANCE_ADJUSTMENT_ERROR,
        payload: { ...params, error: 'Could not find appliance' },
      });
      return;
    }

    if (!appliance.isReady) {
      await dispatch(stopAppliance({ haId }));
      await dispatch(getAppliancePrograms({ haId }));
    }
    const program = selectProgramForAdjustment(params, appliance);
    dispatch({
      type: APPLIANCE_ADJUSTMENT_START,
      payload: { ...params, program },
    });

    try {
      if (!haId) {
        const message = 'Please make sure your oven is connected to the HomeConnect app';
        dispatch({
          type: APPLIANCE_ADJUSTMENT_ERROR,
          payload: { ...params, program, error: { message } },
        });
        return;
      }

      let programInfo;
      let setpointTemp;
      let setpointUnit;
      let warnings;
      try {
        programInfo = await api.getApplianceProgram({ hostname, haId, token, program });

        // Check that the temperature setting is in range
        ({ setpointUnit, setpointTemp, warnings } = checkTemperatureInSupportedRange(
          params,
          programInfo,
        ));
      } catch (err) {
        console.warn('Could not check program supports the requested temperature', err.message);
        setpointUnit = params.unit || '°C';
        if (params.temperature_c && params.temperature_f) {
          setpointTemp = setpointUnit === '°C' ? params.temperature_c : params.temperature_f;
        } else {
          setpointTemp = params.value || 0;
        }
        warnings = [];
      }
      const options = [
        {
          key: 'Cooking.Oven.Option.SetpointTemperature',
          value: setpointTemp,
          unit: setpointUnit,
        },
      ];

      if (params.duration) {
        try {
          options.push({
            key: 'BSH.Common.Option.Duration',
            value: toSeconds(parse(params.duration)),
            unit: 'seconds',
          });
        } catch (err) {
          // Assume the time doesn't need to be set
        }
      } else {
        options.push({
          key: 'BSH.Common.Option.Duration',
          value: 3600,
          unit: 'seconds',
        });
      }
      if (params.rapidHeating && programInfo) {
        if (programInfo.options.find(option => option.key === 'Cooking.Oven.Option.FastPreHeat')) {
          options.push({
            key: 'Cooking.Oven.Option.FastPreHeat',
            value: !!params.rapidHeating,
          });
        }
      }
      if (params.addedMoisture) {
        if (
          programInfo.options.find(option => option.key === 'Cooking.Oven.Option.SteamAssistLevel')
        ) {
          let value;
          switch (params.addedMoisture.toLowerCase()) {
            case 'high':
              value = 'Cooking.Oven.EnumType.AddedSteam.High';
              break;
            case 'medium':
              value = 'Cooking.Oven.EnumType.AddedSteam.Medium';
              break;
            case 'low':
              value = 'Cooking.Oven.EnumType.AddedSteam.Low';
              break;
            default:
              value = 'Cooking.Oven.EnumType.AddedSteam.Off';
              break;
          }
          options.push({
            key: 'Cooking.Oven.Option.SteamAssistLevel',
            value,
          });
        }
      }

      let availableOptions;
      let awaitingManualStart = false;
      if (appliance.remoteControlActive && appliance.remoteStartAllowed) {
        await api.activateProgram({ hostname, haId, token, program, options });
        availableOptions = await api.getActiveProgramOptions({ hostname, haId, token });
      } else {
        await api.selectProgram({ hostname, haId, token, program, options });
        availableOptions = await api.getSelectedProgramOptions({ hostname, haId, token });
        awaitingManualStart = true;
      }
      dispatch({
        type: APPLIANCE_ADJUSTMENT_SUCCESS,
        payload: {
          ...params,
          options: availableOptions,
          warnings,
          programInfo,
          awaitingManualStart,
        },
      });
    } catch (error) {
      dispatch({
        type: APPLIANCE_ADJUSTMENT_ERROR,
        payload: { ...params, error: error.cause || error },
      });
    }
  };
}

export function setCurrentStep({ currentStepIndex }) {
  return async (dispatch, getState) => {
    const { recipe, viewingRecipe } = getState().homeConnect;
    if (recipe && viewingRecipe && recipe.id === viewingRecipe.id) {
      stopPolling();
      await dispatch({
        type: HOME_CONNECT_SET_CURRENT_STEP,
        payload: { currentStepIndex },
      });
      localStorage.setItem('homeConnect.currentStepIndex', getState().homeConnect.currentStepIndex);
    }
  };
}

export function nextStep() {
  return async (dispatch, getState) => {
    stopPolling();
    await dispatch({
      type: HOME_CONNECT_NEXT_STEP,
    });
    localStorage.setItem('homeConnect.currentStepIndex', getState().homeConnect.currentStepIndex);
  };
}

export function restoreState() {
  return async dispatch => {
    if (window && localStorage) {
      let recipe;
      let currentStepIndex;
      let activeStepIndex;
      let recipeInProgress;
      try {
        recipe = JSON.parse(localStorage.getItem('homeConnect.recipe'));
        currentStepIndex = parseInt(localStorage.getItem('homeConnect.currentStepIndex'), 10);
        activeStepIndex = parseInt(localStorage.getItem('homeConnect.activeStepIndex'), 10);
        recipeInProgress = localStorage.getItem('homeConnect.recipeInProgress') === 'true';
      } catch (err) {
        console.warn('Could not restore HomeConnect recipe state', err);
      }
      if (recipe) {
        await dispatch({
          type: HOME_CONNECT_SET_RECIPE,
          payload: { ...recipe, recipeInProgress },
        });
        if (currentStepIndex) {
          await dispatch({
            type: HOME_CONNECT_SET_CURRENT_STEP,
            payload: { currentStepIndex, activeStepIndex },
          });
        }
      } else {
        dispatch({
          type: HOME_CONNECT_CLEAR_RECIPE,
          payload: null,
        });
      }
    }
  };
}

export function executeCurrentStep({ haId }) {
  return async (dispatch, getState) => {
    const { currentStep, currentStepIndex, appliances } = getState().homeConnect;

    if (currentStep) {
      const { overrides, ...adjustment } = currentStep;
      let override;
      if (overrides) {
        override = overrides.find(
          _override => _override.brand === appliances[haId].brand.toLowerCase(),
        );
      }
      const { setting, duration, type, skip } = override || adjustment;
      if (skip || !setting) {
        await dispatch(nextStep({ haId }));
        return;
      }
      const {
        mode,
        unit,
        value,
        rapidHeating,
        addedMoisture,
        temperature_c,
        temperature_f,
      } = setting;
      const durationValue = duration ? duration.value : null;

      if (!durationValue) {
        startPolling(dispatch, getState, {
          haId,
          key: 'Cooking.Oven.Status.CurrentCavityTemperature',
          interval: 30000,
        });
      } else {
        stopPolling();
      }

      if (mode === 'off') {
        dispatch(stopAppliance({ haId }));
      } else {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem('homeConnect.recipeInProgress', 'true');
          window.localStorage.setItem('homeConnect.activeStepIndex', currentStepIndex);
        }
        dispatch(
          adjustAppliance({
            haId,
            type,
            mode,
            unit,
            value: parseInt(value, 10),
            temperature_c,
            temperature_f,
            duration: durationValue,
            rapidHeating: rapidHeating || setting.mode === 'preheat',
            addedMoisture,
            activeStepIndex: currentStepIndex,
          }),
        );
      }
    }
  };
}

export function clearRecipe({ haId, stopCooking, showNextRecipe }) {
  return async (dispatch, getState) => {
    const { recipeInProgress, viewingRecipe } = getState().homeConnect;
    if (!recipeInProgress || stopCooking) {
      dispatch({
        type: HOME_CONNECT_CLEAR_RECIPE,
        payload: null,
      });
      if (haId && stopCooking) {
        stopPolling();
        dispatch(stopAppliance({ haId }));
      }
      if (window && window.localStorage) {
        localStorage.removeItem('homeConnect.recipe');
        localStorage.removeItem('homeConnect.currentStepIndex');
        localStorage.removeItem('homeConnect.activeStepIndex');
        localStorage.removeItem('homeConnect.recipeInProgress');
      }
      if (showNextRecipe && viewingRecipe) {
        dispatch({
          type: HOME_CONNECT_SET_RECIPE,
          payload: viewingRecipe,
        });
      }
    }
  };
}

export function monitorAppliance(params = {}) {
  return async (dispatch, getState) => {
    const { token, hostname, appliances } = getConfig(getState);
    const { haId } = params;
    if (!haId || !token) {
      return;
    }
    if (appliances && appliances[haId]) {
      dispatch({
        type: HOME_CONNECT_EVENT_CHANNEL_CONNECTING,
        payload: { haId },
      });
      const evtSource = api.getApplianceEventSource({ hostname, haId, token });

      const openHandler = () => {
        dispatch({
          type: HOME_CONNECT_EVENT_CHANNEL_OPEN,
          payload: { haId },
        });
      };
      evtSource.addEventListener('open', openHandler);
      evtSource.onopen = openHandler;

      const notificationHandler = evt => {
        if (
          evt.type === 'DISCONNECTED' ||
          evt.type === 'CONNECTED' ||
          // Recover from Gaggenau connection error when the oven is not in lights on mode
          appliances[haId]?.stateFetch?.error?.key ===
            'SDK.Error.HomeAppliance.Connection.Initialization.Failed'
        ) {
          dispatch(getAppliances());
        } else {
          const notifications = JSON.parse(evt.data);
          notifications.items
            .map(notification => ({
              type: HOME_CONNECT_NOTIFICATION,
              payload: { haId, notification },
            }))
            .forEach(dispatch);
        }
      };
      evtSource.addEventListener('NOTIFY', notificationHandler);
      evtSource.addEventListener('EVENT', notificationHandler);
      evtSource.addEventListener('STATUS', notificationHandler);
      evtSource.addEventListener('DISCONNECTED', notificationHandler);
      evtSource.addEventListener('KEEP-ALIVE', () => {
        console.info('Received Home-Connect keep alive');
      });

      let errorCount = 0;
      const errorHandler = error => {
        console.warn('Home Connect event channel error', error.status, error);
        error.preventDefault();
        dispatch({
          type: HOME_CONNECT_EVENT_CHANNEL_ERROR,
          payload: { haId, error },
        });
        errorCount += 1;
        if (errorCount > 5) {
          console.warn(
            'Too many errors from this eventSource. Closing it down to avoid API limit exhaustion.',
          );
          evtSource.close();
          // TODO re-establish the event channel here?
        }
      };

      evtSource.addEventListener('error', errorHandler);

      evtSource.onerror = errorHandler;

      eventSources[haId] = evtSource;
    } else {
      dispatch({
        type: HOME_CONNECT_EVENT_CHANNEL_ERROR,
        payload: { haId, error: `Appliance not found` },
      });
    }
  };
}

export function stopMonitoringAppliance({ haId }) {
  return async dispatch => {
    if (eventSources[haId]) {
      eventSources[haId].close();
      delete eventSources[haId];
      dispatch({ type: HOME_CONNECT_EVENT_CHANNEL_CLOSE, payload: { haId } });
    }
  };
}

export function disconnect() {
  return async (dispatch, getState) => {
    const { appliances } = getState().homeConnect;
    if (appliances) {
      Object.keys(appliances).forEach(haId => {
        dispatch(stopMonitoringAppliance({ haId }));
      });
    }
    dispatch({
      type: HOME_CONNECT_DISCONNECT,
    });
  };
}

export function handleHomeConnectAuthRedirect(url) {
  return async (dispatch, getState, { fetch }) => {
    const parsed = new URL(url);

    // Pass the auth code to the server, which will exchange it for an access token.
    const code = parsed.searchParams.get('code');
    if (code) {
      const { apiUrl } = getState().config;
      const token = await getAuthToken();
      const codeEncoded = encodeURIComponent(code);
      const response = await fetch(
        `${apiUrl}/login/homeconnect/return?code=${codeEncoded}&native=${!!window.cordova}`,
        {
          headers: {
            authorization: token ? `Bearer ${token}` : '',
          },
        },
      );
      const authResponse = await response.json();
      await dispatch({ type: HOME_CONNECT_RESET });
      await dispatch(refreshUser(authResponse));
      await dispatch(getAppliances());
    }
  };
}
